/**
 * agentIdentityService — SENTINEL-CORE-1 / F-SENTINEL-IDENTITY-001
 *
 * Tags every cognition wrapper call (aegis / artemis / council) with a
 * canonical agent identity record: agentId + tenantId + userId + callType +
 * timestamp. Writes to Firestore via DataService and emits
 * SENTINEL-AGENT-ACTION-TAGGED via EventBusService.
 *
 * KTRUST-L1 resilience: every operation is fire-and-forget. Tagging failure
 * NEVER blocks or alters the governed call result.
 *
 * All Firestore writes through DataService — never Firebase directly.
 * tenantId required on every document (FOUNDATION-PURITY).
 */

import DataService from '../DataService';
import EventBusService from '../EventBusService';

const SERVICE_VERSION = '1.0.0';

// ULID-lite: timestamp-prefixed collision-resistant ID sufficient for action records.
function _generateActionId() {
  const now = Date.now();
  const rand = Math.random().toString(36).slice(2, 12).toUpperCase();
  return `ACT_${now.toString(36).toUpperCase()}_${rand}`;
}

/**
 * Tag a cognition wrapper invocation with Sentinel agent identity.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {string} opts.agentId       - e.g. 'aegis', 'artemis-selene', 'kcc'
 * @param {string} opts.callType      - 'aegis' | 'artemis' | 'council'
 * @param {string} opts.module        - calling module name
 * @param {string} opts.decisionType  - decision type slug
 * @param {string|null} [opts.decisionId]  - response decision ID if available
 * @param {string|null} [opts.userId] - end-user identity if provided by caller
 * @returns {Promise<string>} actionId
 */
export async function tagAgentAction(opts) {
  const { tenantId, agentId, callType, module: mod, decisionType, decisionId = null, userId = null } = opts;

  const actionId = _generateActionId();
  const timestamp = new Date().toISOString();

  const record = {
    actionId,
    tenantId,
    agentId,
    callType,
    module: mod,
    decisionType,
    decisionId,
    userId,
    timestamp,
    sentinelVersion: SERVICE_VERSION,
    softDeleted: null,
  };

  await DataService.set(
    `sentinel_agent_actions/${tenantId}/records`,
    actionId,
    record
  );

  await EventBusService.publish({
    eventType: 'sentinel.agent.action.tagged',
    tenantId,
    sourceModule: 'sentinel',
    sourceId: actionId,
    payload: {
      actionId,
      agentId,
      callType,
      module: mod,
      decisionType,
      decisionId,
      userId,
      timestamp,
      sentinelVersion: SERVICE_VERSION,
    },
    actor: { id: agentId, role: 'sentinel_substrate' },
  });

  return actionId;
}

export default { tagAgentAction };
